// src/annihilation/WhisperingGrove3D.jsx
// ============================================================
//  ANNIHILATION — THE WHISPERING GROVE, IN 3D   (perf pass v3)
//  The first field in 3D. A PRESENTATIONAL renderer: the Grove
//  (whisperingGrove.js) keeps owning enemy AI, gathering, combat,
//  and the controller. This file just DRAWS what it's handed —
//  live player pos, the enemies array, the nodes array, floats —
//  in three.js. It is NOT a second source of truth.
//
//  PERF LAWS (v3) — the choppiness post-mortem:
//    · MOVEMENT NEVER RE-RENDERS. The controller's posRef /
//      facingRef flow straight into useFrame. React reconciles
//      NOTHING while you hold a key. (v2 re-rendered the entire
//      Canvas tree at 9Hz through the setPos state — that was
//      the chop.) "Delete the degree of freedom, not the bug."
//    · GroveScene is memo'd. The parent zone still re-renders
//      for HUD/prompts; the 3D tree only reconciles when enemies,
//      nodes, floats, or an action (swing/dodge) actually change.
//    · Identity law downstream: the zone's AI/bleed ticks bail
//      with the SAME array when calm, and unchanged enemies keep
//      the SAME object — so memo(EnemyActor) truly bails.
//    · All smoothing is delta-based (frame-rate independent) and
//      the player glides at CONSTANT tile speed instead of a
//      lerp that surged and stalled between grid steps.
//    · Node ready-glow is driven imperatively in useFrame — a
//      node ripening does not touch React at all.
//    · Static layers stay memo'd; no per-node dynamic lights;
//      light count fixed (sun + fills + hollow + two doorways);
//      shadows 1024, trunks + main canopy only; dpr capped 1.5.
// ============================================================

import { memo, useMemo, useRef, useEffect, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import {
  PlayerBody, EnemyMesh, Floaty, Label, GroundBlob, FollowRig, TouchControls, AbilityWorldFX,
  mulberry32, hashId, canvasTex, isTouch,
} from "./fold3dKit";
import { levelForXp, tierForLevel } from "./stats";

// ── world scale ──────────────────────────────────────────────
const UNIT = 2.4;   // world units per grid tile (field reads open)
const STEP_MS = 110; // must mirror PlayerController FEEL.stepMs
const WALK_SPEED = UNIT / (STEP_MS / 1000); // constant glide, u/s
const DASH_GAP = UNIT * 1.5;  // gap beyond this = a dodge — catch up fast
const DASH_MULT = 3.2;

// grid → world, centered; grid +y → world +z. (.invert = world → grid,
// used by mouse-look to turn a ground-plane hit back into tile space.)
function makeWorld(bounds) {
  const cx = (bounds.minX + bounds.maxX) / 2;
  const cy = (bounds.minY + bounds.maxY) / 2;
  const fn = (gx, gy) => [(gx - cx) * UNIT, 0, (gy - cy) * UNIT];
  fn.invert = (wx, wz) => ({ x: wx / UNIT + cx, y: wz / UNIT + cy });
  return fn;
}

// region → mood: [wall/ground tint, canopy height band, fog feel]
const REGION_MOOD = {
  fringe:      { ground: "#2e3a1e", canopy: [3.5, 5.0], tint: "#3d4a24" },
  deepwood:    { ground: "#243018", canopy: [5.0, 7.5], tint: "#2a3a1e" },
  fallen_gate: { ground: "#1e2416", canopy: [7.0, 10.0], tint: "#242a1a" },
};
const moodOf = (id) => REGION_MOOD[id] || REGION_MOOD.fringe;

// ── ground: tiled forest floor ───────────────────────────────
function groundTex() {
  return canvasTex("grove_ground", 256, 256, (g, w, h) => {
    g.fillStyle = "#26301a"; g.fillRect(0, 0, w, h);
    const R = mulberry32(71);
    for (let i = 0; i < 1200; i++) {
      const x = R() * w, y = R() * h;
      g.fillStyle = R() > 0.5 ? "rgba(60,80,36,0.4)" : "rgba(16,22,10,0.5)";
      g.fillRect(x, y, 2 + R() * 3, 2 + R() * 3);
    }
    for (let i = 0; i < 60; i++) {
      g.fillStyle = `rgba(${140 + R() * 60},${80 + R() * 40},30,0.5)`;
      g.beginPath(); g.ellipse(R() * w, R() * h, 3, 5, R() * 3, 0, 7); g.fill();
    }
  });
}

const GroveGround = memo(function GroveGround({ bounds }) {
  const tex = useMemo(groundTex, []);
  useMemo(() => { tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.repeat.set(8, 6); }, [tex]);
  const w = (bounds.maxX - bounds.minX + 4) * UNIT;
  const d = (bounds.maxY - bounds.minY + 4) * UNIT;
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
      <planeGeometry args={[w, d]} />
      <meshStandardMaterial map={tex} roughness={1} />
    </mesh>
  );
});

// region wash plates so the three regions read from above
const RegionWashes = memo(function RegionWashes({ regions, world, bounds }) {
  return (
    <group>
      {regions.map((r) => {
        const x0 = world(r.minX, bounds.minY);
        const x1 = world(r.maxX, bounds.maxY);
        const cx = (x0[0] + x1[0]) / 2;
        const w = (r.maxX - r.minX + 1) * UNIT;
        const d = (bounds.maxY - bounds.minY + 2) * UNIT;
        return (
          <mesh key={r.id} rotation={[-Math.PI / 2, 0, 0]} position={[cx, 0.02, 0]}>
            <planeGeometry args={[w, d]} />
            <meshBasicMaterial color={moodOf(r.id).tint} transparent opacity={0.16} />
          </mesh>
        );
      })}
    </group>
  );
});

// birch trees — obstacles, height per region band, seeded lean.
// Shadow budget: trunk + main canopy cast; bands and the small
// accent blob do not (invisible difference, real savings).
const Trees = memo(function Trees({ trees, world, regionAt }) {
  return (
    <group>
      {trees.map((t, i) => {
        const [x, , z] = world(t.x, t.y);
        const region = regionAt(t.x);
        const [lo, hi] = moodOf(region.id).canopy;
        const R = mulberry32(hashId(`${t.x},${t.y}`) + 9);
        const h = lo + (hi - lo) * R();
        const lean = (R() - 0.5) * 0.12;
        return (
          <group key={i} position={[x, 0, z]} rotation={[lean, R() * 6, 0]}>
            {/* trunk */}
            <mesh position={[0, h / 2, 0]} castShadow>
              <cylinderGeometry args={[0.14, 0.2, h, 7]} />
              <meshStandardMaterial color="#d8d0c0" roughness={1} />
            </mesh>
            {/* birch banding */}
            <mesh position={[0, h * 0.6, 0]}><cylinderGeometry args={[0.145, 0.145, 0.1, 7]} /><meshStandardMaterial color="#2a2620" /></mesh>
            <mesh position={[0, h * 0.35, 0]}><cylinderGeometry args={[0.17, 0.17, 0.08, 7]} /><meshStandardMaterial color="#2a2620" /></mesh>
            {/* canopy */}
            <mesh position={[0, h + 0.6, 0]} castShadow>
              <sphereGeometry args={[1.1 + R() * 0.4, 8, 7]} />
              <meshStandardMaterial color={R() > 0.5 ? "#3a5226" : "#2f471f"} roughness={1} />
            </mesh>
            <mesh position={[0.4, h + 1.1, 0.3]}>
              <sphereGeometry args={[0.7, 7, 6]} />
              <meshStandardMaterial color="#436024" roughness={1} />
            </mesh>
          </group>
        );
      })}
    </group>
  );
});

// The Silent Hollow — a lit safe clearing (nothing hunts here)
const Hollow = memo(function Hollow({ hollow, world }) {
  const ring = useRef();
  const [x, , z] = world(hollow.x, hollow.y);
  useFrame((s) => { if (ring.current) ring.current.material.opacity = 0.3 + Math.sin(s.clock.elapsedTime * 1.5) * 0.1; });
  return (
    <group position={[x, 0, z]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.05, 0]}>
        <circleGeometry args={[hollow.r * UNIT, 32]} />
        <meshBasicMaterial color="#9fe8c8" transparent opacity={0.14} />
      </mesh>
      <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.06, 0]}>
        <ringGeometry args={[hollow.r * UNIT - 0.3, hollow.r * UNIT, 40]} />
        <meshBasicMaterial color="#bfffe0" transparent opacity={0.35} toneMapped={false} />
      </mesh>
      <pointLight position={[0, 3, 0]} color="#9fe8c8" intensity={2.2} distance={hollow.r * UNIT * 2.5} />
      <Label position={[0, 2.6, 0]} text="The Silent Hollow" accent="#9fe8c8" small z={[14, 6]} />
    </group>
  );
});

// gathering nodes — glowing motes when ready, dim when spent.
// PERF (v3): fully imperative. Ready-state is a clock comparison
// against readyAt done in useFrame — a node ripening ten minutes
// after a gather updates its own materials without React hearing
// about it. Still no per-node lights; the octahedron's emissive
// heat + glow disc sell the beacon in the fog.
const NodeMote = memo(function NodeMote({ node, world }) {
  const grp = useRef();
  const coreMat = useRef();
  const discMat = useRef();
  const [x, , z] = world(node.pos.x, node.pos.y);
  const iron = node.materialId === "fold_iron";
  const col = iron ? "#c9a84c" : "#8fd18a";
  useFrame((s) => {
    if (grp.current) {
      grp.current.position.y = 0.5 + Math.sin(s.clock.elapsedTime * 2 + x) * 0.12;
    }
    const ready = (node.readyAt || 0) <= Date.now();
    if (coreMat.current) {
      coreMat.current.emissiveIntensity = ready ? 1.4 : 0.05;
      coreMat.current.opacity = ready ? 0.95 : 0.25;
    }
    if (discMat.current) discMat.current.opacity = ready ? 0.3 : 0.05;
  });
  return (
    <group position={[x, 0, z]}>
      <mesh ref={grp} position={[0, 0.5, 0]}>
        <octahedronGeometry args={[iron ? 0.3 : 0.24, 0]} />
        <meshStandardMaterial ref={coreMat} color={col} emissive={col} emissiveIntensity={0.05} transparent opacity={0.25} toneMapped={false} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
        <circleGeometry args={[0.5, 16]} />
        <meshBasicMaterial ref={discMat} color={col} transparent opacity={0.05} toneMapped={false} />
      </mesh>
    </group>
  );
});

const Nodes = memo(function Nodes({ nodes, world }) {
  return (
    <group>
      {nodes.map((n) => <NodeMote key={n.id} node={n} world={world} />)}
    </group>
  );
});

// A generic doorway/portal marker (village exit + dungeon mouth)
const Doorway = memo(function Doorway({ world, spot, color, label }) {
  const [x, , z] = world(spot.x, spot.y);
  const ring = useRef();
  useFrame((s) => { if (ring.current) { const sc = 1 + Math.sin(s.clock.elapsedTime * 2) * 0.06; ring.current.scale.set(sc, sc, sc); } });
  return (
    <group position={[x, 0, z]}>
      <mesh position={[-1.1, 1.4, 0]} castShadow><boxGeometry args={[0.3, 2.8, 0.3]} /><meshStandardMaterial color="#2a2018" roughness={1} /></mesh>
      <mesh position={[1.1, 1.4, 0]} castShadow><boxGeometry args={[0.3, 2.8, 0.3]} /><meshStandardMaterial color="#2a2018" roughness={1} /></mesh>
      <mesh position={[0, 2.9, 0]} castShadow><boxGeometry args={[2.5, 0.3, 0.3]} /><meshStandardMaterial color="#2a2018" roughness={1} /></mesh>
      <mesh ref={ring} position={[0, 1.45, 0]}><planeGeometry args={[1.9, 2.6]} /><meshBasicMaterial color={color} transparent opacity={0.4} side={THREE.DoubleSide} toneMapped={false} /></mesh>
      <pointLight position={[0, 1.5, 0.3]} color={color} intensity={2} distance={8} />
      <Label position={[0, 3.6, 0]} text={label} accent={color} />
    </group>
  );
});

// ── enemies: drawn from the live array. Position glides toward
// the grid target with delta-based smoothing so movement is even
// at any frame rate. Facing derived from motion. Telegraph → flash.
// PERF (v3): memo'd — the zone's ticks preserve object identity
// for unchanged enemies, so an idle wolf costs zero reconciliation.
const EnemyActor = memo(function EnemyActor({ enemy, world }) {
  const grp = useRef();
  const body = useRef();
  const cur = useRef(new THREE.Vector3(...world(enemy.pos.x, enemy.pos.y)));
  const facing = useRef({ x: 0, y: 1 });
  const target = useRef(new THREE.Vector3(...world(enemy.pos.x, enemy.pos.y)));
  useEffect(() => {
    const [tx, , tz] = world(enemy.pos.x, enemy.pos.y);
    const dx = tx - cur.current.x, dz = tz - cur.current.z;
    if (Math.abs(dx) > 0.001 || Math.abs(dz) > 0.001) facing.current = { x: dx, y: dz };
    target.current.set(tx, 0, tz);
  }, [enemy.pos.x, enemy.pos.y, world]);
  const telegraphing = enemy.state === "telegraph" || enemy.telegraphing;
  useFrame((s, dt) => {
    // frame-rate independent exponential chase (~was lerp 0.2 @60fps)
    const k = 1 - Math.exp(-13 * Math.min(dt, 0.1));
    cur.current.lerp(target.current, k);
    if (grp.current) grp.current.position.copy(cur.current);
    // wind-up tell: the body coils (pulls back + bobs) while telegraphing
    if (body.current) body.current.position.z = telegraphing ? Math.sin(s.clock.elapsedTime * 12) * 0.08 - 0.06 : 0;
  });
  if (!enemy.alive) return null;
  return (
    <group ref={grp}>
      <group ref={body}>
        <EnemyMesh family={enemy.family} role={enemy.role} id={enemy.id} telegraph={telegraphing} facing={facing.current} />
      </group>
      <GroundBlob r={0.4} opacity={0.3} />
      {enemy.anima < (enemy.animaMax || enemy.anima) && (
        <EnemyHealth enemy={enemy} />
      )}
    </group>
  );
});

function EnemyHealth({ enemy }) {
  const frac = Math.max(0, Math.min(1, enemy.anima / (enemy.animaMax || enemy.anima || 1)));
  return (
    <group position={[0, 1.9, 0]}>
      <mesh><boxGeometry args={[0.9, 0.1, 0.02]} /><meshBasicMaterial color="#1a1410" /></mesh>
      <mesh position={[-(0.9 * (1 - frac)) / 2, 0, 0.01]}><boxGeometry args={[0.9 * frac, 0.08, 0.02]} /><meshBasicMaterial color="#e86a5c" toneMapped={false} /></mesh>
    </group>
  );
}

// ── MOUSE-LOOK (v4): full 360° aim. Casts the pointer onto the
// ground plane every frame, inverts it to tile space, and hands
// the controller a normalized aim vector — all through refs, so
// looking around costs React nothing. Desktop only; touch keeps
// movement-steered facing.
const GROUND_PLANE = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
function MouseAim({ onAim, ctrlPosRef, world }) {
  const { raycaster, pointer, camera } = useThree();
  const hit = useRef(new THREE.Vector3());
  const armed = useRef(false); // don't aim until the mouse actually moves
  useEffect(() => {
    const arm = () => { armed.current = true; };
    window.addEventListener("pointermove", arm, { once: true });
    return () => window.removeEventListener("pointermove", arm);
  }, []);
  useFrame(() => {
    if (!armed.current || !onAim || !ctrlPosRef?.current) return;
    raycaster.setFromCamera(pointer, camera);
    if (!raycaster.ray.intersectPlane(GROUND_PLANE, hit.current)) return;
    const g = world.invert(hit.current.x, hit.current.z);
    const p = ctrlPosRef.current;
    const dx = g.x - p.x, dy = g.y - p.y;
    if (Math.hypot(dx, dy) > 0.15) onAim({ x: dx, y: dy });
  });
  return null;
}

// ── the player (v4): reads the controller's live posRef/facingRef
// every frame — movement never touches React. Glides at constant
// tile speed (dash catch-up on dodge gaps); the body YAWS
// imperatively toward the live 360° facing (shortest arc, eased),
// so mouse-look turns are silk and cost zero re-renders.
const FACE_NORTH = { x: 0, y: 1 }; // PlayerBody's internal yaw = 0
function GrovePlayer({ character, tier, ctrlPos, ctrlPosRef, ctrlFacingRef, dim, swing, dodging, cast, ghost, grace, pawsArmed, followRef, world }) {
  const grp = useRef();
  const cur = useRef(new THREE.Vector3(...world(ctrlPos.x, ctrlPos.y)));
  const tmp = useRef(new THREE.Vector3());
  const yaw = useRef(0);
  const [moving, setMoving] = useState(false);
  const movingRef = useRef(false);
  useFrame((_, dt) => {
    const p = ctrlPosRef?.current || ctrlPos;
    const [tx, , tz] = world(p.x, p.y);
    tmp.current.set(tx, 0, tz);
    const gap = cur.current.distanceTo(tmp.current);
    if (gap > 0.001) {
      const speed = gap > DASH_GAP ? WALK_SPEED * DASH_MULT : WALK_SPEED;
      const step = Math.min(gap, speed * Math.min(dt, 0.1));
      cur.current.lerp(tmp.current, step / gap);
    }
    const isMoving = gap > 0.08;
    if (isMoving !== movingRef.current) {
      movingRef.current = isMoving;
      setMoving(isMoving); // rare flip — start/stop only
    }
    // 360° yaw — shortest arc toward the live facing, eased.
    const f = ctrlFacingRef?.current;
    if (f && grp.current) {
      const target = Math.atan2(f.x || 0, f.y || 1);
      let d = target - yaw.current;
      while (d > Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      yaw.current += d * Math.min(1, dt * 14);
      grp.current.rotation.y = yaw.current;
    }
    if (grp.current) grp.current.position.copy(cur.current);
    if (followRef) followRef.current = cur.current;
  });
  const beingId = character?.beingId || null;
  return (
    <group ref={grp}>
      <PlayerBody beingId={beingId} tier={tier} facing={FACE_NORTH} moving={moving} dim={dim} swing={swing} dodging={dodging} cast={cast} ghost={ghost} grace={grace} pawsArmed={pawsArmed} />
    </group>
  );
}

// ── SCENE — memo'd (v3). The zone re-renders freely for its HUD;
// this tree reconciles only when enemies/nodes/floats change or an
// action fires. Movement flows through the refs, invisible to React.
const GroveScene = memo(function GroveScene({ character, tier, ctrlPos, ctrlPosRef, ctrlFacingRef, onAim, dim, swing, dodging, cast, ghost, grace, pawsArmed, enemies, nodes, floats, geo }) {
  const world = useMemo(() => makeWorld(geo.bounds), [geo.bounds]);
  const followRef = useRef(new THREE.Vector3(...world(ctrlPos.x, ctrlPos.y)));

  return (
    <>
      <color attach="background" args={["#0e1408"]} />
      <fog attach="fog" args={["#182010", 20, 70]} />

      <ambientLight intensity={0.5} />
      <hemisphereLight args={["#2a3a1e", "#0e0a06", 0.5]} />
      <directionalLight
        position={[20, 34, 14]} intensity={0.85} castShadow
        shadow-mapSize-width={1024} shadow-mapSize-height={1024}
        shadow-camera-left={-40} shadow-camera-right={40}
        shadow-camera-top={30} shadow-camera-bottom={-30} shadow-camera-far={120}
      />
      <directionalLight position={[-26, 18, -20]} intensity={0.32} color="#b388ff" />

      <GroveGround bounds={geo.bounds} />
      <RegionWashes regions={geo.regions} world={world} bounds={geo.bounds} />
      <Trees trees={geo.trees} world={world} regionAt={geo.regionAt} />
      <Hollow hollow={geo.hollow} world={world} />
      <Nodes nodes={nodes} world={world} />

      <Doorway world={world} spot={geo.villageExit} color="#e8a33d" label="← The Old Fold" />
      <Doorway world={world} spot={geo.dungeonDoor} color="#7c5cff" label="The Fallen Gate ↓" />

      {enemies.map((e) => <EnemyActor key={e.id} enemy={e} world={world} />)}

      {floats.map((f) => {
        const [x, , z] = world(f.x, f.y);
        return <Floaty key={f.id} position={[x, 1.6, z]} text={f.text} color={f.color} />;
      })}

      {onAim && !isTouch() && <MouseAim onAim={onAim} ctrlPosRef={ctrlPosRef} world={world} />}
      <GrovePlayer character={character} tier={tier} ctrlPos={ctrlPos} ctrlPosRef={ctrlPosRef} ctrlFacingRef={ctrlFacingRef} dim={dim} swing={swing} dodging={dodging} cast={cast} ghost={ghost} grace={grace} pawsArmed={pawsArmed} followRef={followRef} world={world} />
      {/* FX-1 — world-space cast visuals (the Shadow, the Presence, the Grace) */}
      <AbilityWorldFX cast={cast} world={world} />
      <FollowRig followRef={followRef} trail={[0.001, 13, 15]} look={[0, 1.0, 0]} />
    </>
  );
}, (prev, next) =>
  // custom bail: ctrlPos is a fresh object every grid step — it is
  // spawn-only here, so identity churn on it must NOT reconcile.
  prev.character === next.character &&
  prev.tier === next.tier &&
  prev.ctrlPosRef === next.ctrlPosRef &&
  prev.ctrlFacingRef === next.ctrlFacingRef &&
  prev.onAim === next.onAim &&
  prev.dim === next.dim &&
  prev.swing === next.swing &&
  prev.dodging === next.dodging &&
  prev.cast === next.cast &&
  prev.ghost === next.ghost &&
  prev.grace === next.grace &&
  prev.pawsArmed === next.pawsArmed &&
  prev.enemies === next.enemies &&
  prev.nodes === next.nodes &&
  prev.floats === next.floats &&
  prev.geo === next.geo
);

// ── EXPORT — same essential inputs the 2D grove renders from.
// The grove passes its live state down; this owns no game logic.
// v3 adds ctrlPosRef / ctrlFacingRef (from usePlayerController) —
// the hot path. ctrlPos/ctrlFacing remain as spawn + fallback.
export default function WhisperingGrove3D({
  character = null,
  ctrlPos = { x: 2, y: 8 },
  ctrlFacing = { x: 1, y: 0 },
  ctrlPosRef = null,   // ctrl.posRef — live movement, read in useFrame
  ctrlFacingRef = null, // ctrl.facingRef — live facing, read in useFrame
  onAim = null,        // ctrl.setAim — v4 mouse-look writes 360° aim vectors
  dim = false,
  swing = null,        // ctrl.lastSwing — fires the 3D strike
  dodging = false,     // ctrl.dodging — bends into a roll
  cast = null,         // FX-1: ctrl.lastCast — fires cast postures + world FX
  ghost = false,       // FX-1: ctrl.ghostForm — the incorporeal treatment
  grace = false,       // FX-1: ctrl.graceArmed — King's Grace, THE aura, worn
  pawsArmed = false,   // FX-1: ctrl.pawsArmed — the paw-fire while armed
  keysRef = null,      // ctrl.keysRef — TouchControls writes into it
  onAttack = null,     // bind to ctrl.attack (mobile ⚔)
  onDodge = null,      // bind to ctrl.dodge  (mobile ↻)
  onInteract = null,   // gather / descend    (mobile ✦)
  enemies = [],
  nodes = [],
  floats = [],
  geo,                 // { bounds, regions, regionAt, trees, hollow, villageExit, dungeonDoor, materials }
  prompt = null,
  feltEvent = null,
  downed = false,
  onBack = () => {},
}) {
  const tier = useMemo(() => {
    if (!character) return null;
    const { level } = levelForXp(character.xp || 0);
    return tierForLevel(level);
  }, [character]);

  // fallback refs so the scene works even if a zone hasn't been
  // upgraded to pass the controller refs yet (prop-driven mode).
  const posFallback = useRef(ctrlPos);
  posFallback.current = ctrlPos;
  const facingFallback = useRef(ctrlFacing);
  facingFallback.current = ctrlFacing;

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0e1408" }}>
      <Canvas shadows camera={{ position: [0, 16, 20], fov: 52 }} dpr={[1, 1.5]}>
        <GroveScene
          character={character} tier={tier}
          ctrlPos={ctrlPos}
          ctrlPosRef={ctrlPosRef || posFallback}
          ctrlFacingRef={ctrlFacingRef || facingFallback}
          onAim={onAim}
          dim={dim} swing={swing} dodging={dodging}
          cast={cast} ghost={ghost} grace={grace} pawsArmed={pawsArmed}
          enemies={enemies} nodes={nodes} floats={floats} geo={geo}
        />
      </Canvas>

      <TouchControls keysRef={keysRef} onAttack={onAttack} onDodge={onDodge} onInteract={onInteract} accent="#7fe8c8" />

      <button style={backBtn} onClick={onBack}>← Back to the Gate</button>

      {prompt ? <div style={promptBox}>{prompt}</div> : null}
      {feltEvent ? <div style={feltBox}>{feltEvent}</div> : null}
      {downed ? (
        <div style={downVeil}>
          <div style={downText}>the Grove goes quiet</div>
          <div style={downSub}>you wake at the Old Fold — nothing is lost, nothing is rested</div>
        </div>
      ) : null}
      <div style={hintBox}>WASD / arrows to move · J/Space strike · K/Shift dodge · 2–5 cast · E gather</div>
    </div>
  );
}

// ── chrome styles (match the 2D grove's language) ────────────
const backBtn = {
  position: "absolute", top: 14, left: 14, zIndex: 20,
  padding: "8px 14px", background: "rgba(20,16,25,0.9)", color: "#e9ddc4",
  border: "2px solid #c9a84c", borderRadius: 8, fontFamily: "Georgia, serif",
  fontWeight: 700, fontSize: 13, cursor: "pointer", boxShadow: "0 3px 0 #141019",
};
const promptBox = {
  position: "absolute", bottom: 58, left: "50%", transform: "translateX(-50%)",
  zIndex: 20, background: "rgba(20,16,25,0.92)", color: "#f0d060",
  border: "1px solid #c9a84c", borderRadius: 10, padding: "8px 18px",
  fontFamily: "Georgia, serif", fontSize: 14, fontWeight: 700, textShadow: "0 1px 2px #000",
};
const feltBox = {
  position: "absolute", top: 70, left: "50%", transform: "translateX(-50%)",
  zIndex: 20, background: "rgba(20,16,25,0.85)", color: "#9fe8c8",
  border: "1px solid #7fe8c8", borderRadius: 10, padding: "7px 16px",
  fontFamily: "Georgia, serif", fontSize: 13, letterSpacing: 0.5, textShadow: "0 1px 2px #000",
};
const hintBox = {
  position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)",
  zIndex: 20, background: "rgba(20,16,25,0.82)", color: "#e9ddc4",
  border: "1px solid #c9a84c", borderRadius: 10, padding: "7px 16px",
  fontFamily: "Georgia, serif", fontSize: 12.5, letterSpacing: 0.4, textShadow: "0 1px 2px #000",
};
const downVeil = {
  position: "absolute", inset: 0, zIndex: 40, display: "flex", flexDirection: "column",
  alignItems: "center", justifyContent: "center", gap: 10,
  background: "rgba(6,8,4,0.82)", backdropFilter: "blur(3px)",
};
const downText = { color: "#cfe8bf", fontFamily: "Georgia, serif", fontSize: 30, fontWeight: 800, textShadow: "0 2px 8px #000" };
const downSub = { color: "#8fb08a", fontFamily: "Georgia, serif", fontSize: 15, textShadow: "0 1px 3px #000" };
